/**
 * Patches `confirmed` on matching purchase order rows across cached V2 search queries
 * (paginated + infinite) and matching detail caches. Used so confirm/clear from the
 * list or entity stays reflected when opening a PO or returning to the list within staleTime.
 *
 * @param {{ setQueriesData: Function }|null|undefined} queryClient React Query client.
 * @param {string|number|null|undefined} purchaseOrderId Purchase order id to update.
 * @param {string|null} confirmedAt Confirmation timestamp, or null when cleared.
 */
export function patchConfirmedInPurchaseOrderSearchCaches(queryClient, purchaseOrderId, confirmedAt) {
    if (!queryClient?.setQueriesData || purchaseOrderId == null) {
        return;
    }

    const idKey = String(purchaseOrderId);

    const patchRows = (rows) => {
        if (!Array.isArray(rows)) return rows;
        let didChange = false;
        const nextRows = rows.map((row) => {
            if (row?.id == null || String(row.id) !== idKey) {
                return row;
            }
            didChange = true;
            return { ...row, confirmed: confirmedAt };
        });
        return didChange ? nextRows : rows;
    };

    queryClient.setQueriesData(
        {
            predicate: (query) =>
                Array.isArray(query?.queryKey) &&
                query.queryKey[0] === 'purchaseOrders' &&
                query.queryKey[1] === 'detail' &&
                String(query.queryKey[2]) === idKey,
        },
        (old) => {
            if (!old || typeof old !== 'object') {
                return old;
            }
            return { ...old, confirmed: confirmedAt };
        },
    );

    queryClient.setQueriesData({ queryKey: ['purchaseOrders', 'v2', 'search'] }, (old) => {
        if (!old || typeof old !== 'object') {
            return old;
        }

        if (Array.isArray(old.rows)) {
            const rows = patchRows(old.rows);
            return rows === old.rows ? old : { ...old, rows };
        }

        if (Array.isArray(old.pages)) {
            let didChange = false;
            const pages = old.pages.map((page) => {
                if (!page || typeof page !== 'object' || !Array.isArray(page.rows)) {
                    return page;
                }
                const rows = patchRows(page.rows);
                if (rows === page.rows) {
                    return page;
                }
                didChange = true;
                return { ...page, rows };
            });
            return didChange ? { ...old, pages } : old;
        }

        return old;
    });
}
